import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  Paper,
  Stack,
  Typography,
  darken,
  lighten,
} from '@mui/material';
import AddComment from './AddComment';
import Comment from './Comment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import moment from 'moment';
import TrackedVimeo from './TrackedVimeo';
import { useCourseCore } from '../../../core/modules/course';
import { Class } from '../../../core/interfaces/class.interface';
import { Comment as CourseComment } from '../../../core/interfaces/comment.interface';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { useClassCore } from '../../../core/modules/class';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ClassSummaryPanel from './ClassSummaryPanel';
import { ClassSummary, parseClassSummary } from '../../../core/interfaces/classSummary.interface';



interface Props {
  classes: Class[];
  currentClass: number;
  onNext?: () => void;
}



const ClassInfo = (props: Props) => {
  const { classes, currentClass, onNext } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const hasNextClass = currentClass < classes.length;

  const handleNext = useCallback(() => {
    onNext?.();
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [onNext]);
  const {
    course,
    classComments,
    viewed,
    postView,
    deleteView,
    getViews,
    getClassComments,
    getClassSummary,
  } = useCourseCore();
  const { postClassSeen } = useClassCore();



  const clazz = useMemo(() => {
    const current = classes[currentClass - 1];
    if (!current) {
      return null;
    }



    const apiClass = current as Class & {
      videos?: Class['videoLinks'];
      links?: Class['urlLinks'];
      files?: Class['fileLinks'];
    };



    return {
      ...current,
      categories: current.categories ?? [],
      videoLinks: apiClass.videoLinks ?? apiClass.videos ?? [],
      urlLinks: apiClass.urlLinks ?? apiClass.links ?? [],
      fileLinks: apiClass.fileLinks ?? apiClass.files ?? [],
    };
  }, [classes, currentClass]);



  const isViewed = useMemo(() => {
    return viewed.includes(classes[currentClass - 1]?.id);
  }, [classes, currentClass, viewed]);



  useEffect(() => {
    if (clazz && process.env.REACT_APP_MIXPANEL_TOKEN) {
      mixpanel.track('Viewing class', {
        'id': clazz.id,
        'name': clazz.name,
        'courseId': course?.id,
        'courseName': course?.name,
      });
      mixpanel.track('Viewing course', {
        'id': course?.id,
        'name': course?.name,
      });
    }
  }, [clazz, course]);



  const toggleView = useCallback(() => {
    if (isViewed) {
      deleteView(course?.id!, classes[currentClass - 1].id, () => {
        getViews(course?.id!);
      });
    } else {
      postView(course?.id!, classes[currentClass - 1].id, () => {
        getViews(course?.id!);
      });
    }
  }, [classes, course?.id, currentClass, deleteView, getViews, isViewed, postView]);



  const postSeen = useCallback(() => {
    if (classes[currentClass - 1]?.automationMailGroup) {
      postClassSeen(classes[currentClass - 1].id);
    }
  }, [classes, currentClass, postClassSeen]);



  // Resumen de la clase (hecho con IA desde el video y los PDFs). Parte cerrado; el botón lo abre y cierra.
  const [summary, setSummary] = useState<ClassSummary | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [seekTo, setSeekTo] = useState<{ seconds: number; video?: string | null; nonce: number } | null>(null);

  useEffect(() => {
    setSummary(null);
    setSummaryOpen(false);
    setSeekTo(null);
    if (!course?.id || !clazz?.id) {
      return;
    }
    let active = true;
    getClassSummary(String(course.id), String(clazz.id), (raw) => {
      if (active) {
        setSummary(parseClassSummary(raw));
      }
    });
    return () => {
      active = false;
    };
  }, [clazz?.id, course?.id, getClassSummary]);

  const trackSummary = useCallback(
    (action: 'open' | 'close' | 'seek', seconds?: number) => {
      if (clazz && process.env.REACT_APP_MIXPANEL_TOKEN) {
        mixpanel.track('Class summary', {
          action,
          classId: clazz.id,
          className: clazz.name,
          courseId: course?.id,
          courseName: course?.name,
          ...(seconds !== undefined ? { seconds } : {}),
        });
      }
    },
    [clazz, course?.id, course?.name],
  );

  const toggleSummary = useCallback(() => {
    setSummaryOpen((open) => {
      trackSummary(open ? 'close' : 'open');
      return !open;
    });
  }, [trackSummary]);

  const seekVideo = useCallback(
    (seconds: number, video?: string | null) => {
      setSeekTo({ seconds, video, nonce: Date.now() });
      trackSummary('seek', seconds);
    },
    [trackSummary],
  );

  const onAddingComment = useCallback(() => {
    if (course?.id && clazz?.id) {
      getClassComments(course.id, clazz.id);
    }
  }, [clazz?.id, course?.id, getClassComments]);



  const isMentorComment = useCallback(
    (comment?: CourseComment) =>
      Boolean(comment?.user?.role === 'admin' || comment?.user?.role === 'professor'),
    [],
  );



  if (!clazz) {
    return null;
  }

  const resourceColumns = 12 / Math.max(1, [clazz.urlLinks?.length, clazz.fileLinks?.length, summary].filter(Boolean).length);



  return (
    <Box
      ref={containerRef}
      className="w-full flex justify-center p-16 pb-64 sm:p-24 sm:pb-64 md:p-40 md:pb-64"
      sx={{ minHeight: '100%', backgroundColor: '#f8fafc' }}
    >
      <Paper
        className="w-full mx-auto sm:my-8 lg:mt-16"
        sx={{
          maxWidth: 1120,
          overflow: 'hidden',
          borderRadius: { xs: 4, md: 5 },
          border: '1px solid rgba(148,163,184,0.22)',
          backgroundColor: '#fff',
          boxShadow: '0 18px 50px -40px rgba(15,23,42,0.35)',
        }}
      >
        <Box
          sx={{
            p: { xs: 3, md: 5 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#fff',
          }}
        >
          <Box
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap={3}
            alignItems={{ md: 'center' }}
          >
            <Box flex={1}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 15 }} />}
                  label={moment(clazz.createdAt).format('DD/MM/YYYY')}
                  variant="outlined"
                  sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}
                />
                <Chip
                  size="small"
                  label={`Clase ${currentClass}/${classes.length}`}
                  color="secondary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.015em',
                  lineHeight: 1.18,
                  fontSize: { xs: 22, md: 32 },
                  maxWidth: 780,
                  color: '#0f172a',
                }}
              >
                {clazz.name}
              </Typography>
              {clazz.description ? (
                <Typography
                  variant="body1"
                  sx={{ mt: 2, color: 'text.secondary', maxWidth: 780, fontSize: { md: 17 }, lineHeight: 1.6 }}
                >
                  {clazz.description}
                </Typography>
              ) : null}
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, flexShrink: 0 }}
            >
              <Button
                variant="contained"
                color={isViewed ? 'success' : 'secondary'}
                startIcon={isViewed ? <CheckCircleOutlineIcon /> : <PanoramaFishEyeIcon />}
                onClick={toggleView}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  py: 1.2,
                }}
              >
                {isViewed ? 'Completada' : 'Marcar como concluida'}
              </Button>
              {hasNextClass && onNext ? (
                <Button
                  variant="outlined"
                  color="secondary"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleNext}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.2,
                  }}
                >
                  Próxima Clase
                </Button>
              ) : null}
            </Stack>
          </Box>



          {clazz.categories?.length ? (
            <Box display="flex" flexWrap="wrap" gap={1} mt={2.5}>
              {clazz.categories.map((category) => (
                <Chip
                  key={category.id}
                  label={category.name}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    color: darken(category.color, 0.4),
                    backgroundColor: lighten(category.color, 0.8),
                  }}
                />
              ))}
            </Box>
          ) : null}
        </Box>



        <Box sx={{ p: { xs: 3, md: 5 } }}>
          {clazz.videoLinks?.length ? (
            <Box
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                border: '1px solid rgba(15,23,42,0.08)',
                backgroundColor: '#0f172a',
                boxShadow: '0 28px 70px -45px rgba(15,23,42,0.9)',
              }}
            >
              {clazz.videoLinks.map((video) => (
                <TrackedVimeo
                  key={`${clazz.id}-${video.id}`}
                  videoId={video.id}
                  url={video.url}
                  classId={clazz.id}
                  className={clazz.name}
                  courseId={course?.id}
                  courseName={course?.name}
                  onPlaying={postSeen}
                  seekTo={
                    seekTo && (!seekTo.video || seekTo.video === String(video.url) || clazz.videoLinks.length === 1)
                      ? seekTo
                      : undefined
                  }
                />
              ))}
            </Box>
          ) : null}



          {(clazz.urlLinks?.length || clazz.fileLinks?.length || summary) ? (
            <Grid container spacing={3} mt={clazz.videoLinks?.length ? 1 : -3}>
              {clazz.urlLinks?.length ? (
                <Grid item xs={12} md={resourceColumns}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 4,
                      height: '100%',
                      borderColor: 'rgba(2,132,199,0.16)',
                      background: 'linear-gradient(180deg, rgba(240,249,255,0.75), rgba(255,255,255,0.96))',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <LinkOutlinedIcon color="info" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Links externos
                      </Typography>
                    </Stack>
                    <Stack spacing={1.25}>
                      {clazz.urlLinks.map((file) => (
                        <Box
                          key={file.id}
                          component="a"
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            borderRadius: 3,
                            border: '1px solid rgba(2,132,199,0.12)',
                            backgroundColor: '#fff',
                            p: 1.5,
                            textDecoration: 'none',
                            color: 'inherit',
                            transition: 'all 160ms ease',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 14px 30px -24px rgba(2,132,199,0.75)',
                            },
                          }}
                        >
                          <Box minWidth={0}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {file.url}
                            </Typography>
                          </Box>
                          <OpenInNewOutlinedIcon fontSize="small" color="info" />
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              ) : null}
              {clazz.fileLinks?.length ? (
                <Grid item xs={12} md={resourceColumns}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 4,
                      height: '100%',
                      borderColor: 'rgba(5,150,105,0.16)',
                      background: 'linear-gradient(180deg, rgba(236,253,245,0.72), rgba(255,255,255,0.96))',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <InsertDriveFileOutlinedIcon color="success" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Archivos descargables
                      </Typography>
                    </Stack>
                    <Stack spacing={1.25}>
                      {clazz.fileLinks.map((file) => (
                        <Box
                          key={file.id}
                          display="flex"
                          flexDirection={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ xs: 'stretch', sm: 'center' }}
                          gap={{ xs: 1, sm: 2 }}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(5,150,105,0.12)',
                            backgroundColor: '#fff',
                            p: 1.5,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: 'anywhere', minWidth: 0 }}>
                            {file.name}
                          </Typography>
                          <Button size="small" variant="contained" color="success" onClick={() => window.open(file.url, '_blank')}>
                            Descargar
                          </Button>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              ) : null}
              {summary ? (
                <Grid item xs={12} md={resourceColumns}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      borderColor: (theme) => alpha(theme.palette.secondary.main, 0.18),
                      background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.secondary.main, 0.07)}, rgba(255,255,255,0.96))`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <SubjectOutlinedIcon sx={{ color: 'secondary.main' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Resumen de la clase
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Las ideas más importantes en {summary.readingMinutes} minutos de lectura, con el minuto del video donde
                      se explica cada una.
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={toggleSummary}
                      aria-expanded={summaryOpen}
                      aria-controls="class-summary"
                      sx={{ alignSelf: 'flex-start', borderRadius: 999, px: 3 }}
                    >
                      {summaryOpen ? 'Cerrar resumen' : 'Leer resumen'}
                    </Button>
                  </Paper>
                </Grid>
              ) : null}
            </Grid>
          ) : null}

          {summary ? (
            <Collapse in={summaryOpen} unmountOnExit>
              <Box id="class-summary" mt={3}>
                <ClassSummaryPanel summary={summary} onSeek={seekVideo} />
              </Box>
            </Collapse>
          ) : null}



          <Box mt={4} p={0} width="100%">
            <Box
              sx={{
                borderRadius: 0,
                p: { xs: 0, md: 2 },
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
              }}
            >
              <Typography variant="overline" sx={{ letterSpacing: 2, color: 'secondary.dark' }}>
                Foro de la clase
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e1b4b', mb: 1, fontSize: { xs: 19, md: 22 } }}>
                Comparte tus dudas y aprendizajes
              </Typography>
            </Box>
            <Box
              sx={{
                borderRadius: 0,
                p: { xs: 0, md: 2 },
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
              }}
            >
              <Stack spacing={2}>
                <AddComment classId={clazz.id} onSuccess={onAddingComment} />
                {classComments.map((comment) => (
                  <Comment
                    key={comment.id}
                    comment={comment}
                    classId={clazz.id}
                    depth={0}
                    isMentor={isMentorComment(comment)}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};



export default ClassInfo;


